import { Component } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormArray, Validators } from '@angular/forms';
import { ReactiveFormsModule } from '@angular/forms';
import { forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { ConfirmationService, MessageService } from 'primeng/api';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { CalendarModule } from 'primeng/calendar';
import { OverlayPanelModule } from 'primeng/overlaypanel';
import { ordersService } from '../../services/orders.service';
import { HttpClient } from '@angular/common/http';
import { TabService } from 'src/app/shared/tab.service';
import { Router } from '@angular/router';

interface VendorBillTripRow {
  order: any;
  trip: any;
  claim: any;
  orderNumber: string;
  tripNumber: string;
  vendorName: string;
  vehicleNumber: string;
  advance: number;
  baseAmount: number;
  claimAmount: number;
  claimStatus: string;
  vendorCharge: number;
  vendorCharges: any[];
  indeminityEnabled: boolean;
}

@Component({
  selector: 'app-vendor-bill-popup',
  // standalone: true,
  // imports: [
  //   CommonModule,
  //   ReactiveFormsModule,
  //   ProgressSpinnerModule,
  //   CalendarModule,
  //   ConfirmDialogModule,
  //   OverlayPanelModule
  // ],
  // providers: [ConfirmationService],
  templateUrl: './vendor-bill-popup.component.html',
  styleUrls: ['./vendor-bill-popup.component.css']
})
export class VendorBillPopupComponent {
  selectedOrders: any[] = [];
  tripRows: VendorBillTripRow[] = [];
  loading = false;
  saving = false;

  // Fallback shape used when a source API fails/404s so forkJoin never
  // gets torn down by one bad branch (see loadVendorBillingData below).
  private readonly EMPTY_LIST_RESPONSE = { responseObject: { content: [] } };

  invoiceForm = this.fb.group({
    invoiceNo: ['', Validators.required],
    fileUrl: [''],
    invoiceDate: ['', Validators.required],
    accountGstId: [null, Validators.required],
    totalAmount: [0, [Validators.required, Validators.min(0)]],
    advanceAmount: [0, [Validators.required, Validators.min(0)]],
    claimAmount: [0, [Validators.required, Validators.min(0)]],
    vendorCharge: [0, [Validators.required, Validators.min(0)]],
    indeminityTotal: [0],
    balancePayable: [0, [Validators.required, Validators.min(0)]],
    invoiceAmount: [null, [Validators.required, Validators.min(0)]],
    remarks: [''],
    trips: this.fb.array([])
  });

  totals = {
    totalAmount: 0,
    claimAmount: 0,
    advance: 0,
    vendorCharge: 0,
    indeminityTotal: 0,
    balancePayable: 0,
    invoiceAmount: 0
  };
  uploadApi = '';
  uploadingInvoice = false;
  gstList: any[] = [];
  loadingGst = false;
  constructor(
    private fb: FormBuilder,
    private ordersService: ordersService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    public ref: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private http: HttpClient,
    private tabService: TabService,
    private router: Router,
  ) {
    this.selectedOrders = this.config.data?.orders || [];
  }

  ngOnInit(): void {
    this.bindInvoiceCalculations();
    this.loadVendorBillingData();
    this.loadGstList(); 
  }

  get tripsFormArray(): FormArray {
    return this.invoiceForm.get('trips') as FormArray;
  }

  private loadGstList(): void {
    const accountId = this.selectedOrders[0]?.accountId || this.selectedOrders[0]?.account?.id;
  
    if (!accountId) {
      return;
    }
  
    this.loadingGst = true;
  
    this.ordersService.getByAccountId(accountId)
      .pipe(
        map((res: any) => this.extractList(res)),
        catchError(err => {
          console.error('Failed to load GST list', err);
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Failed to load GST details'
          });
          return of([]);
        })
      )
      .subscribe((list: any[]) => {
        this.gstList = list;
        this.loadingGst = false;
      });
  }

  updateVendorBilling(item: any): void {
    this.openVendorBilling('Details', 'vendor-billing/update/' + item.id, item);
  }

  private openVendorBilling(label: string, path: string, vendorBillingData?: any): void {
    this.tabService.addTab({
      label,
      path,
      fullPath: '',
      filterData: []
    });
    this.router.navigate([path], { state: { vendorBillingData } });
  }
  
  getGstLabel(gst: any): string {
    return gst?.gstNumber || gst?.gstNo || gst?.gstin || gst?.name || '--';
  }

  loadVendorBillingData(): void {
    const orderIds = this.selectedOrders.map(order => order?.id).filter(Boolean);

    if (!orderIds.length) {
      return;
    }

    const filters = [{ key: 'ORDER_ID_IN', value: orderIds.join(',') }];
    this.loading = true;

    // IMPORTANT: forkJoin is fail-fast — if ANY source errors (e.g. claims
    // 404 when no claims exist yet), it unsubscribes from the others too,
    // which is why trip/additional-charge calls were seen as "cancelled".
    // We catch errors on each source individually and resolve them to an
    // empty response instead, so a missing claim never blocks the rest.
    forkJoin({
      tripData: this.ordersService.getTripInfo(filters).pipe(
        catchError(err => {
          console.error('Failed to load trip info', err);
          return of(this.EMPTY_LIST_RESPONSE);
        })
      ),
      claimData: this.ordersService.getClaimsForVendorBilling(filters).pipe(
        catchError(err => {
          // 404 here just means "no claims raised yet" — not a real error.
          if (err?.status !== 404) {
            console.error('Failed to load claims', err);
          }
          return of(this.EMPTY_LIST_RESPONSE);
        })
      ),
      additionalChargeData: this.ordersService.getAdditionalCharges(filters, 0, 100, 'id', 'ASC').pipe(
        catchError(err => {
          console.error('Failed to load additional charges', err);
          return of(this.EMPTY_LIST_RESPONSE);
        })
      )
    }).subscribe({
      next: ({ tripData, claimData, additionalChargeData }: any) => {
        const trips = this.extractList(tripData);
        const claims = this.extractList(claimData);
        const additionalCharges = this.extractList(additionalChargeData);
        this.tripRows = this.selectedOrders.map(order => this.mapTripRow(order, trips, claims, additionalCharges));
        this.buildTripsFormArray();
        this.calculateTotals();
        this.loading = false;
      },
      error: (err: any) => {
        // Safety net only — each branch above already self-heals, so this
        // should rarely fire now.
        this.loading = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.responseMessage || 'Failed to load vendor billing details'
        });
      }
    });
  }

  generateBill(): void {
    this.recalculateInvoiceAmounts();

    if (this.invoiceForm.invalid) {
      this.invoiceForm.markAllAsTouched();
      return;
    }

    const invoiceAmount = this.toNumber(this.invoiceForm.get('invoiceAmount')?.value);
    const balancePayable = this.toNumber(this.invoiceForm.get('balancePayable')?.value);

    if (invoiceAmount !== balancePayable) {
      this.confirmationService.confirm({
        header: 'Confirm',
        message: `Amount Mismatch! Invoice amount is ${invoiceAmount} and balance payable ${balancePayable} is different. Do you want to continue?`,
        icon: 'pi pi-exclamation-triangle',
        acceptLabel: 'Yes',
        rejectLabel: 'No',
        accept: () => this.submitVendorBill(),
        reject: () => {
          // Stay on the same screen, no action needed.
        }
      });
      return;
    }

    this.submitVendorBill();
  }

  isInvalid(field: string): boolean {
    const control = this.invoiceForm.get(field);
    return !!control && control.touched && control.invalid;
  }

  isIndeminityInvalid(index: number): boolean {
    const control = this.tripsFormArray.at(index)?.get('indeminityAmount');
    return !!control && control.touched && control.invalid;
  }

  formatAmount(value: any): string {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(Number(value || 0));
  }

  getStatusClass(status: string): string {
    const value = (status || '').toUpperCase();

    if (value.includes('APPROVED') || value.includes('CLOSED') || value.includes('RECEIVED')) {
      return 'status-success';
    }

    if (value.includes('PENDING') || value.includes('DRAFT')) {
      return 'status-warning';
    }

    if (value.includes('REJECTED') || value.includes('OPEN')) {
      return 'status-danger';
    }

    return 'status-default';
  }

  /** Label shown per line item in the vendor-charge breakdown popup. */
  getChargeLabel(charge: any): string {
    return charge?.chargeType || charge?.additionalChargeType || charge?.type || charge?.name || 'Charge';
  }

  /** Optional remarks/description shown under the charge label in the popup. */
  getChargeRemarks(charge: any): string {
    return charge?.remarks || charge?.description || '';
  }

  private submitVendorBill(): void {
    const payload = this.buildVendorBillingPayload();

    this.saving = true;
    this.ordersService.saveVendorBilling(payload).subscribe({
      next: (res: any) => {
        this.saving = false;
        this.updateVendorBilling(res?.responseObject)
        this.messageService.add({
          severity: 'success',
          summary: 'Success',
          detail: res?.responseMessage || 'Vendor bill created successfully'
        });
        this.ref.close(res || payload);
      },
      error: (err: any) => {
        this.saving = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: err?.error?.responseMessage || 'Failed to create vendor bill'
        });
      }
    });
  }

  private mapTripRow(order: any, trips: any[], claims: any[], additionalCharges: any[]): VendorBillTripRow {
    const trip = trips.find(item => this.isSameId(item?.orderId || item?.order?.id, order?.id)) || {};
    const claim = claims.find(item =>
      this.isSameId(item?.orderId || item?.order?.id, order?.id) ||
      this.isSameId(item?.tripId || item?.trip?.id, trip?.id)
    ) || {};
    const vendorCharges = additionalCharges.filter(item => {
      const matchesOrder = this.isSameId(item?.orderId || item?.order?.id, order?.id);
      const matchesTrip = this.isSameId(item?.tripId || item?.trip?.id, trip?.id);
      return (matchesOrder || matchesTrip) && this.isTruthy(item?.chargeToVendor);
    });
    const vendorCharge = vendorCharges.reduce((sum, item) => {
      return sum + this.toNumber(item?.vendorCharge);
    }, 0);

    return {
      order,
      trip,
      claim,
      orderNumber: order?.orderNumber || order?.orderNo || `ORD-${order?.id}`,
      tripNumber: trip?.tripNumber || trip?.tripNo || order?.tripNumber || '--',
      vendorName: order?.vendorName || trip?.vendorName || '--',
      vehicleNumber: trip?.vehicleNumber || trip?.vehicleNo || trip?.vehicle || '--',
      advance: this.toNumber(trip?.advance || trip?.advanceAmount || order?.advance || order?.advanceAmount),
      baseAmount: this.toNumber(order?.procurementProvidedAmount || order?.procurementPrice || order?.bidAmount || order?.baseAmount || trip?.baseAmount),
      // When claims 404 (no claims raised), `claims` will be [] and `claim`
      // resolves to {} here — these fall back to 0 / 'No Claim' cleanly.
      claimAmount: this.toNumber(claim?.totalAmount || claim?.claimAmount || claim?.amount),
      claimStatus: claim?.status || claim?.claimStatus || 'No Claim',
      vendorCharge,
      vendorCharges,
      indeminityEnabled: this.isTruthy(trip?.indeminityEnabled ?? trip?.indeminityEnabled)
    };
  }

  private buildTripsFormArray(): void {
    const array = this.tripsFormArray;
    array.clear();
    this.tripRows.forEach(row => {
      array.push(this.fb.group({
        tripId: [row.trip?.id],
        indeminityAmount: [
          0,
          row.indeminityEnabled ? [Validators.required, Validators.min(0)] : []
        ]
      }));
    });
  }

  private buildTripsPayload(): { tripId: number; indeminityAmount: number }[] {
    return this.tripsFormArray.controls
      .map(control => ({
        tripId: control.get('tripId')?.value,
        indeminityAmount: this.toNumber(control.get('indeminityAmount')?.value)
      }))
      .filter(trip => !!trip.tripId);
  }

  private computeIndeminityTotal(): number {
    return this.tripsFormArray.controls.reduce((sum, control) => {
      return sum + this.toNumber(control.get('indeminityAmount')?.value);
    }, 0);
  }

  private calculateTotals(): void {
    this.totals = this.tripRows.reduce((total, row) => {
      total.totalAmount += row.baseAmount;
      total.claimAmount += row.claimAmount;
      total.advance += row.advance;
      total.vendorCharge += row.vendorCharge;
      return total;
    }, { totalAmount: 0, claimAmount: 0, advance: 0, vendorCharge: 0, indeminityTotal: 0, balancePayable: 0, invoiceAmount: 0 });

    this.totals.indeminityTotal = this.computeIndeminityTotal();

    this.totals.balancePayable = this.getBalancePayable(
      this.totals.totalAmount,
      this.totals.advance,
      this.totals.claimAmount,
      this.totals.vendorCharge,
      this.totals.indeminityTotal
    );

    this.invoiceForm.patchValue({
      totalAmount: this.totals.totalAmount,
      advanceAmount: this.totals.advance,
      claimAmount: this.totals.claimAmount,
      vendorCharge: this.totals.vendorCharge,
      indeminityTotal: this.totals.indeminityTotal,
      balancePayable: this.totals.balancePayable
    }, { emitEvent: false });
  }

  private bindInvoiceCalculations(): void {
    ['totalAmount', 'advanceAmount', 'claimAmount', 'vendorCharge'].forEach(field => {
      this.invoiceForm.get(field)?.valueChanges.subscribe(() => this.recalculateInvoiceAmounts());
    });

    this.tripsFormArray.valueChanges.subscribe(() => this.recalculateInvoiceAmounts());
  }

  private recalculateInvoiceAmounts(): void {
    const indeminityTotal = this.computeIndeminityTotal();
    const balancePayable = this.getBalancePayable(
      this.invoiceForm.get('totalAmount')?.value,
      this.invoiceForm.get('advanceAmount')?.value,
      this.invoiceForm.get('claimAmount')?.value,
      this.invoiceForm.get('vendorCharge')?.value,
      indeminityTotal
    );

    this.totals.totalAmount = this.toNumber(this.invoiceForm.get('totalAmount')?.value);
    this.totals.advance = this.toNumber(this.invoiceForm.get('advanceAmount')?.value);
    this.totals.claimAmount = this.toNumber(this.invoiceForm.get('claimAmount')?.value);
    this.totals.vendorCharge = this.toNumber(this.invoiceForm.get('vendorCharge')?.value);
    this.totals.indeminityTotal = indeminityTotal;
    this.totals.balancePayable = balancePayable;

    this.invoiceForm.patchValue({
      indeminityTotal,
      balancePayable
    }, { emitEvent: false });
  }

  // Balance = Total - Advance - Claim + Vendor Charge - Indeminity Total
  private getBalancePayable(totalAmount: any, advanceAmount: any, claimAmount: any, vendorCharge: any, indeminityTotal: any = 0): number {
    return Math.max(
      this.toNumber(totalAmount)
      - this.toNumber(advanceAmount)
      - this.toNumber(claimAmount)
      + this.toNumber(vendorCharge)
      - this.toNumber(indeminityTotal),
      0
    );
  }

  private buildVendorBillingPayload(): any {
    const firstOrder = this.selectedOrders[0] || {};
    return {
      vendorId: firstOrder?.vendorId || firstOrder?.vendor?.id || null,
      accountId: firstOrder?.accountId || firstOrder?.account?.id || null,
      accountGstId: this.invoiceForm.value.accountGstId || null,
      invoiceNumber: this.invoiceForm.value.invoiceNo || '',
      fileUrl: this.invoiceForm.value.fileUrl || '',
      invoiceDate: this.formatInvoiceDate(this.invoiceForm.value.invoiceDate),
      totalAmount: this.toNumber(this.invoiceForm.value.totalAmount),
      invoiceAmount: this.toNumber(this.invoiceForm.value.invoiceAmount),
      status: 'CREATED',
      paymentStatus: 'PENDING',
      remarks: this.invoiceForm.value.remarks || '',
      trips: this.buildTripsPayload()
    };
  }

  private formatInvoiceDate(value: any): string {
    if (!value) {
      return '';
    }

    const date = value instanceof Date ? value : new Date(value);

    if (Number.isNaN(date.getTime())) {
      return value;
    }

    const pad = (num: number): string => String(num).padStart(2, '0');
    const offsetMinutes = -date.getTimezoneOffset();
    const sign = offsetMinutes >= 0 ? '+' : '-';
    const absOffset = Math.abs(offsetMinutes);
    const offset = `${sign}${pad(Math.floor(absOffset / 60))}${pad(absOffset % 60)}`;

    return `${pad(date.getDate())}-${pad(date.getMonth() + 1)}-${date.getFullYear()} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())} ${offset}`;
  }

  private extractList(response: any): any[] {
    const responseObject = response?.responseObject;
    const list = responseObject?.content || responseObject || response?.content || response || [];
    return Array.isArray(list) ? list : [list];
  }

  private isSameId(left: any, right: any): boolean {
    return left !== null && left !== undefined && right !== null && right !== undefined && String(left) === String(right);
  }

  private toNumber(value: any): number {
    return Number(value || 0);
  }

  private isTruthy(value: any): boolean {
    return value === true || value === 1 || value === '1' || String(value).toLowerCase() === 'true';
  }

  onInvoiceFileSelected(event: any): void {
    const file: File = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    this.uploadingInvoice = true;
    this.uploadFile(file).subscribe({
      next: (url: string) => {
        this.uploadingInvoice = false;
        this.invoiceForm.patchValue({ fileUrl: url });
      },
      error: () => {
        this.uploadingInvoice = false;
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Failed to upload invoice document'
        });
      }
    });
  }

  uploadFile(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    this.uploadApi = this.ordersService.uploadFilesURL();
    return this.http.post(this.uploadApi, formData).pipe(
      map((res: any) =>
        res?.responseObject?.url
        || res?.responseObject?.fileUrl
        || (typeof res?.responseObject === 'string' ? res.responseObject : '')
        || res?.url
        || res?.fileUrl
        || ''
      )
    );
  }
}